// src/layouts/AdminNavDrawer.jsx
import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import Place from "@mui/icons-material/Place";
import PaletteIcon from "@mui/icons-material/Palette";
import CakeIcon from "@mui/icons-material/Cake";
import LayersIcon from "@mui/icons-material/Layers";
import RamenDiningIcon from "@mui/icons-material/RamenDining";
import BakeryDiningIcon from "@mui/icons-material/BakeryDining";
import LunchDiningIcon from "@mui/icons-material/LunchDining";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import InfoIcon from "@mui/icons-material/Info";
import InsightsIcon from "@mui/icons-material/Insights";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import DraftsIcon from "@mui/icons-material/Drafts";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Section = ({ title, children }) => (
  <List
    subheader={
      <ListSubheader component="div" disableSticky>
        {title}
      </ListSubheader>
    }>
    {children}
  </List>
);

export default function AdminNavDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.employee?.role ?? user?.role;
  const canSeeStaffContent = ["admin", "manager", "eventsManager", "staff"];
  const canSeeEvents = ["admin", "eventsManager"];

  const go = (path) => {
    navigate(path);
    onClose?.(); // ✅ always close after navigation
  };

  const Item = ({ icon, label, to, tooltip }) => (
    <Tooltip title={tooltip || ""} placement="right">
      <ListItemButton onClick={() => go(to)}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={label} />
      </ListItemButton>
    </Tooltip>
  );

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: 280,
          boxSizing: "border-box",
          backgroundColor: "#fafafa",
          borderRight: "1px solid #e0e0e0",
        },
      }}>
      <Box
        sx={{
          px: 2,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
        <Typography variant="h6" fontWeight="bold" color="primary">
          Admin
        </Typography>
        <IconButton onClick={onClose} aria-label="Close menu">
          <CloseIcon />
        </IconButton>
      </Box>

      <List>
        <Item icon={<HomeIcon />} label="Home" to="/admin/dashboard" />
      </List>

      <Divider />

      {canSeeStaffContent.includes(role) && (
        <Section title="📦 Orders & Loyalty">
          <Item icon={<ShoppingBagIcon />} label="Orders" to="/admin/booking" />
          <Item
            icon={<CardGiftcardIcon />}
            label="Loyalty Members"
            to="/admin/loyalty-program"
          />
        </Section>
      )}

      {(role === "admin" || role === "manager") && (
        <>
          <Divider />
          <Section title="🛒 Supply & Shopping">
            <Item
              icon={<InventoryIcon />}
              label="Supply Items"
              to="/admin/supply-items"
            />
            <Item
              icon={<ReceiptLongIcon />}
              label="Submit Grocery List"
              to="/admin/shopping-list"
            />
            <Item
              icon={<Place />}
              label="Driver Checklist"
              to="/admin/driver-checklist"
            />
          </Section>
        </>
      )}

      {canSeeEvents.includes(role) && (
        <>
          <Divider />
          <Section title="🎉 Events">
            <Item
              icon={<PaletteIcon />}
              label="Decorating Classes"
              to="/admin/decorating-classes"
            />
          </Section>
        </>
      )}

      {role === "admin" && (
        <>
          <Divider />
          <Section title="🍰 Cake Products">
            <Item
              icon={<CakeIcon />}
              label="Specialty Cakes"
              to="/admin/specialty-cakes"
            />
            <Item
              icon={<LayersIcon />}
              label="Standard Cakes"
              to="/admin/standard-cakes"
            />
            <Item
              icon={<RamenDiningIcon />}
              label="Cheesecakes"
              to="/admin/cheesecakes"
            />
            <Item
              icon={<BakeryDiningIcon />}
              label="Patisserie"
              to="/admin/patisserie"
            />
            <Item
              icon={<LunchDiningIcon />}
              label="Graze Boxes"
              to="/admin/graze-boxes"
            />
            <Item
              icon={<LunchDiningIcon />}
              label="Assorted Dessert Boxes"
              to="/admin/assorted-dessert-boxes"
            />
          </Section>

          <Divider />
          <Section title="🧁 Accessories">
            <Item
              icon={<EmojiObjectsIcon />}
              label="Accessories"
              to="/admin/accessories"
            />
          </Section>

          <Divider />
          <Section title="🛍️ Add-ons & Upsells">
            <Item
              icon={<ShoppingBagIcon />}
              label="Upsell Items"
              to="/admin/upsell-items"
            />
            <Item
              icon={<InsightsIcon />}
              label="Cost Calculator"
              to="/admin/cost-calculator"
            />
            <Item
              icon={<InsightsIcon />}
              label="Quick Cost Calculator"
              to="/admin/quick-cost-calculator"
            />
            <Divider />
            <Section title="🧾 Orders">
              <Item
                icon={<ReceiptLongIcon />}
                label="Manual Orders"
                to="/admin/manual-orders"
              />
              <Item
                icon={<DraftsIcon />}
                label="Draft Orders"
                to="/admin/draft-orders"
              />
            </Section>
          </Section>

          <Divider />
          <Section title="⚙️ Configuration">
            <Item icon={<PaletteIcon />} label="Flavors" to="/admin/flavors" />
            <Item icon={<LayersIcon />} label="Sizes" to="/admin/sizes" />
            <Item
              icon={<RestaurantIcon />}
              label="Ingredients"
              to="/admin/ingredients"
            />
            <Item
              icon={<EmojiObjectsIcon />}
              label="Cake Attributes"
              to="/admin/cake-attributes"
            />
            <Item
              icon={<EmojiObjectsIcon />}
              label="Disclaimers"
              to="/admin/disclaimers"
            />
            <Item
              icon={<InventoryIcon />}
              label="Suppliers"
              to="/admin/suppliers"
            />
            <Item icon={<Place />} label="Store Locations" to="/admin/stores" />
            <Item
              icon={<LocalOfferIcon />}
              label="Discount Rules"
              to="/admin/discounts"
            />
          </Section>

          <Divider />
          <Section title="📋 Metadata">
            <Item icon={<InfoIcon />} label="Allergens" to="/admin/allergens" />
            <Item
              icon={<LocalOfferIcon />}
              label="Dietary Tags"
              to="/admin/dietary-tags"
            />
          </Section>

          <Divider />
          <Section title="🛡️ System">
            <Item icon={<LayersIcon />} label="Logs" to="/admin/logs" />
            <Item
              icon={<AssessmentOutlinedIcon />}
              label="Business Overivew"
              to="/admin/business-overview"
            />
            <Item
              icon={<LayersIcon />}
              label="Employees"
              to="/admin/employees"
            />
            <Item
              icon={<InfoIcon />}
              label="Contact Messages"
              to="/admin/contact-messages"
            />
          </Section>
        </>
      )}
    </Drawer>
  );
}
